import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useFormik } from "formik";
import * as yup from "yup";
import { useForgotPasswordMutation } from "../../../app/authApiSlice";
import toast from "../../../helpers/toast";

function ForgotPassword() {
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [submitted, setSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: yup.object({
      email: yup.string().email("Enter a valid email").required("Required"),
    }),
    onSubmit: async (values) => {
      try {
        await forgotPassword(values).unwrap();
      } catch (_) {
        // Silently ignore — the backend always returns 200 to avoid leaking account info.
      } finally {
        setSubmitted(true);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#eee",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& form > *": { mb: 2.5 },
        "& .heading": {
          mt: 2,
          color: "#27ae60",
          fontWeight: "bolder",
          fontVariant: "smallCaps",
          fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
          fontSize: "2.5rem",
        },
        "& .subHeading": { color: "#9faecb" },
        "& button": {
          background: "#27ae60",
          "& :hover": { background: "#27ae60" },
        },
      }}
    >
      <Stack alignItems="center" spacing={2} sx={{ width: "100%" }}>
        <StyledPaper>
          <Box className="paperBody" sx={{ p: 4 }}>
            {submitted ? (
              <Stack spacing={2}>
                <Alert severity="success">
                  If that email address is registered, a reset link has been sent.
                  Please check your inbox.
                </Alert>
                <Typography variant="body2" sx={{ textAlign: "center" }}>
                  <Link to="/login" style={{ color: "#27ae60" }}>
                    Back to Login
                  </Link>
                </Typography>
              </Stack>
            ) : (
              <Box component="form" onSubmit={formik.handleSubmit}>
                <Typography variant="h1" className="heading">
                  FORGOT PASSWORD
                </Typography>
                <Typography className="subHeading">
                  Enter your email address and we'll send you a reset link.
                </Typography>
                <FormControl error fullWidth>
                  <StyledTextField
                    autoFocus
                    variant="outlined"
                    id="email"
                    name="email"
                    placeholder="Email address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                  />
                  <FormHelperText variant="error">
                    {formik.touched.email && formik.errors.email}
                  </FormHelperText>
                </FormControl>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <Button type="submit" variant="contained">
                      Send Reset Link
                    </Button>
                  )}
                  <Typography variant="body2">
                    <Link to="/login" style={{ color: "#27ae60" }}>
                      Back to Login
                    </Link>
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </StyledPaper>
      </Stack>
    </Box>
  );
}

export default ForgotPassword;
